import { useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Dna } from 'lucide-react';

import { useFeatureMutations } from '@/features/patient-record/hooks/useFeatureMutations';
import { defaultFeatureInput } from '@/features/patient-record/lib/featureForm';
import { patientQueries } from '@/features/patient-record/queries';
import { useNoFindingsStore } from '@/features/patient-record/stores/noFindingsStore';
import type { FeatureViewModel } from '@/features/patient-record/types';

import { ClinicalSection, type ClinicalSectionLabels } from '../ClinicalSection';
import { ConfirmDeleteDialog } from '../_shared/ConfirmDeleteDialog';

import { FeatureRow } from './FeatureRow';
import { GroupedFeatureList } from './GroupedFeatureList';
import { HpoBrowseDialog } from './HpoBrowseDialog';
import {
  PhenotypesToolbar,
  type FeatureFilter,
  type FilterChip,
} from './PhenotypesToolbar';

interface PhenotypesSectionProps {
  readonly guid: string;
}

/**
 * HPO phenotypes for a patient. Inline workflow: a persistent search at
 * the top of the section adds a new phenotype on selection (defaults to
 * `present`); rows expand in-place for qualifier editing; the status
 * badge cycles present → absent → unknown.
 *
 * The live `/persons/{guid}/features` endpoint multiplexes phenotypes
 * with other record kinds (cancer, disorder, mode-of-inheritance, …) —
 * we filter to `kind === 'PHENOTYPE'` (and accept `null` for BFF / legacy
 * rows). Term entry uses the shared `VocabularyTermPicker` against the live
 * HPO vocabulary-service search (PTV2-1042).
 */
export function PhenotypesSection({ guid }: PhenotypesSectionProps): ReactElement {
  const { t } = useTranslation();
  const query = useQuery({ ...patientQueries.features(guid), placeholderData: keepPreviousData });
  const [filter, setFilter] = useState<FeatureFilter>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<FeatureViewModel | null>(null);
  const [pickerCategory, setPickerCategory] = useState<string>('');
  const [groupByCategory, setGroupByCategory] = useState(false);
  const [browseOpen, setBrowseOpen] = useState(false);
  const { create, remove } = useFeatureMutations(guid);
  const noFindings = useNoFindingsStore((s) => s.byGuid[guid] ?? false);
  const setNoFindings = useNoFindingsStore((s) => s.set);

  const phenotypeItems = useMemo(
    () =>
      (query.data ?? []).filter((f) => f.kind === 'PHENOTYPE' || f.kind === null),
    [query.data],
  );
  // Duplicate-guard sets shared by the add picker (PTV2-1119) and the
  // Browse dialog (PTV2-1122). `excludedHpoIds` greys out rows whose HPO
  // id is already attached; `excludedLabels` covers the custom-term case
  // where a free-text phenotype was attached without an id.
  const excludedHpoIds = useMemo(
    () =>
      new Set(
        phenotypeItems
          .map((f) => f.hpoId)
          .filter((id): id is string => id.length > 0),
      ),
    [phenotypeItems],
  );
  const excludedLabels = useMemo(
    () =>
      new Set(
        phenotypeItems
          .filter((f) => f.hpoId.length === 0)
          .map((f) => f.label.trim().toLowerCase())
          .filter((label) => label.length > 0),
      ),
    [phenotypeItems],
  );
  // When "no findings" is toggled, phenotype rows are hidden (not deleted)
  // and counts collapse to zero. Toggling off restores the same rows.
  const effectiveItems = noFindings ? [] : phenotypeItems;
  const presentCount = effectiveItems.filter((f) => f.status === 'present').length;
  const absentCount = effectiveItems.filter((f) => f.status === 'absent').length;
  const unknownCount = effectiveItems.filter((f) => f.status === 'unknown').length;

  const filteredItems = useMemo(
    () =>
      filter === 'all'
        ? effectiveItems
        : effectiveItems.filter((f) => f.status === filter),
    [filter, effectiveItems],
  );
  const filteredQuery = { ...query, data: filteredItems };

  const labels: ClinicalSectionLabels = {
    title: t('patient.phenotypes.heading'),
    empty: noFindings
      ? t('patient.phenotypes.noFindings.empty')
      : t('patient.phenotypes.empty'),
    emptyIcon: <Dna className="h-5 w-5" aria-hidden="true" />,
    loading: t('patient.phenotypes.loading'),
    errorHeading: t('patient.phenotypes.error.heading'),
    errorMessage: t('patient.phenotypes.error.message'),
    codeLabel: t('routes.patient.error.codeLabel'),
    retryLabel: t('routes.patient.error.retry'),
  };

  const filterButtons: readonly FilterChip[] = [
    { id: 'all', label: t('patient.phenotypes.filterAll'), count: effectiveItems.length },
    { id: 'present', label: t('patient.phenotypes.filterPresent'), count: presentCount },
    { id: 'absent', label: t('patient.phenotypes.filterAbsent'), count: absentCount },
    { id: 'unknown', label: t('patient.phenotypes.filterUnknown'), count: unknownCount },
  ];

  const addPhenotype = (term: { id: string; label: string } | null): void => {
    if (term === null) return;
    // Belt-and-suspenders dedupe (PTV2-1119): the picker greys out duplicates
    // already, but guard the mutation in case a stale click slips through.
    if (term.id.length > 0 && excludedHpoIds.has(term.id)) return;
    if (term.id.length === 0 && excludedLabels.has(term.label.trim().toLowerCase())) return;
    create.mutate(defaultFeatureInput(term));
  };

  // When toggling no-findings on with existing rows present, warn before
  // hiding (the rows aren't deleted; toggling off restores them).
  // PTV2-1051 #4: scope-reduced from the spec's "warn and clear" — we hide,
  // don't clear, so a single confirm suffices.
  const handleNoFindingsChange = (next: boolean): void => {
    if (next && phenotypeItems.length > 0) {
      const ok = window.confirm(t('patient.phenotypes.noFindings.confirmHide'));
      if (!ok) return;
    }
    setNoFindings(guid, next);
  };

  const toolbar = (
    <PhenotypesToolbar
      guid={guid}
      noFindings={noFindings}
      onChangeNoFindings={handleNoFindingsChange}
      pickerCategory={pickerCategory}
      onChangePickerCategory={setPickerCategory}
      groupByCategory={groupByCategory}
      onChangeGroupByCategory={setGroupByCategory}
      canGroup={!noFindings && effectiveItems.length > 0}
      onAddPhenotype={addPhenotype}
      pickerDisabled={create.isPending || noFindings}
      excludedHpoIds={excludedHpoIds}
      excludedLabels={excludedLabels}
      onOpenBrowser={() => {
        setBrowseOpen(true);
      }}
      browseDisabled={create.isPending || noFindings}
      filter={filter}
      onChangeFilter={setFilter}
      filterButtons={filterButtons}
      showFilterChips={effectiveItems.length > 0 || phenotypeItems.length > 0}
    />
  );

  const renderFeatureRow = (feature: FeatureViewModel): ReactElement => (
    <FeatureRow
      guid={guid}
      feature={feature}
      expanded={expandedId === feature.id}
      onToggleExpand={() => {
        setExpandedId((prev) => (prev === feature.id ? null : feature.id));
      }}
      onDelete={() => {
        setPendingDelete(feature);
      }}
    />
  );

  return (
    <>
      <ClinicalSection
        id="phenotypes"
        labels={labels}
        query={filteredQuery}
        summary={phenotypeItems.map((f) => ({ label: f.label, value: f.hpoId || '—', filled: true }))}
        toolbar={toolbar}
        itemKey={(feature) => feature.id}
        renderItem={renderFeatureRow}
        {...(groupByCategory && filteredItems.length > 0
          ? {
              customBody: (
                <GroupedFeatureList
                  features={filteredItems}
                  renderItem={renderFeatureRow}
                />
              ),
            }
          : {})}
      />
      <HpoBrowseDialog
        open={browseOpen}
        onOpenChange={setBrowseOpen}
        excludedHpoIds={excludedHpoIds}
        onAddTerm={addPhenotype}
      />
      {pendingDelete !== null && (
        <ConfirmDeleteDialog
          title={t('patient.phenotypes.confirmDeleteTitle')}
          message={t('patient.phenotypes.confirmDeleteMessage', {
            label: pendingDelete.label,
          })}
          confirmLabel={t('patient.phenotypes.delete')}
          busy={remove.isPending}
          onCancel={() => {
            setPendingDelete(null);
          }}
          onConfirm={() => {
            remove.mutate(pendingDelete.id, {
              onSettled: () => {
                setPendingDelete(null);
              },
            });
          }}
        />
      )}
    </>
  );
}
